import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { EmptyState } from "@arcadeai/design-system/components/ui/molecules/empty-state";
import { McpToolCard } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/tool-card";
import { McpToolkitGroupHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/toolkit-group-header";
import { type ReactNode, useMemo } from "react";
import type { ColumnBreakpoint } from "../hooks/use-element-columns";
import { useIdCallback } from "../hooks/use-id-callback";
import { plural, toToolCardProps } from "../mappers";
import { countSelectedTools } from "../selection";
import type { McpTool, ToolGroup } from "../types";
import { type CardSection, VirtualizedCardGrid } from "./virtualized-card-grid";

// Tool card (~120px) + buffer; the virtualizer measures actual heights.
const TOOL_CARD_ROW_HEIGHT = 132;
// Group header (title + count) plus the gap that separates sections.
const GROUP_HEADER_HEIGHT = 44;
// Mirror McpServerPickerToolGrid's @container steps (@xl=576, @4xl=896).
const COLUMN_BREAKPOINTS: ColumnBreakpoint[] = [
  { minWidth: 896, columns: 3 },
  { minWidth: 576, columns: 2 },
];

const toolKey = (tool: McpTool): string => tool.qualified_name;

function useToolCardRenderer(
  onToggleTool: (fqn: string) => void,
  fqns: string[],
  selected: Set<string>
) {
  // Stable, id-bound toggle handlers so the memoized tool cards re-render only
  // when their own selected state changes — not on every parent render.
  const getToggle = useIdCallback(onToggleTool, fqns);
  return (tool: McpTool): ReactNode => (
    <McpToolCard
      {...toToolCardProps(tool)}
      // The card's root is a <button>, which shrink-wraps its content; fill the
      // grid column so tool cards span their cell like the server cards do.
      className="w-full"
      onToggle={getToggle(tool.qualified_name)}
      selected={selected.has(tool.qualified_name)}
    />
  );
}

/** A single, flat virtualized grid of one server's tools (the drill-in view). */
export function ToolCardGrid({
  onToggleTool,
  scrollContainer = null,
  selected,
  tools,
}: {
  onToggleTool: (fqn: string) => void;
  scrollContainer?: HTMLElement | null;
  selected: Set<string>;
  tools: McpTool[];
}) {
  const fqns = useMemo(() => tools.map((t) => t.qualified_name), [tools]);
  const renderTool = useToolCardRenderer(onToggleTool, fqns, selected);
  const sections = useMemo(() => [{ id: "tools", items: tools }], [tools]);

  return (
    <VirtualizedCardGrid
      cardRowHeight={TOOL_CARD_ROW_HEIGHT}
      columnBreakpoints={COLUMN_BREAKPOINTS}
      getItemKey={toolKey}
      label="Tools"
      renderItem={renderTool}
      scrollContainer={scrollContainer}
      sections={sections}
    />
  );
}

/**
 * Tools grouped by server, rendered as one virtualized list (section headers
 * interleaved with card rows) so hundreds of groups / thousands of tools stay
 * fast — a single virtualizer, never one per group.
 */
export function ToolGroups({
  emptyTitle = "No tools available",
  emptyAction,
  groups,
  onOpenServer,
  onToggleTool,
  scrollContainer = null,
  selected,
  showSelectedCount = false,
  trailingCard,
}: {
  emptyTitle?: string;
  emptyAction?: ReactNode;
  groups: ToolGroup[];
  onOpenServer?: (server: string) => void;
  onToggleTool: (fqn: string) => void;
  scrollContainer?: HTMLElement | null;
  selected: Set<string>;
  /**
   * Label each group header as "X of Y selected" rather than a raw tool count.
   * Use when the groups carry a server's full catalog (e.g. the review) so the
   * header reflects the selection, not the number of cards rendered.
   */
  showSelectedCount?: boolean;
  /** Card-sized cell rendered after the groups (e.g. the unoptimized card). */
  trailingCard?: ReactNode;
}) {
  const fqns = useMemo(
    () => groups.flatMap((g) => g.tools.map((t) => t.qualified_name)),
    [groups]
  );
  const renderTool = useToolCardRenderer(onToggleTool, fqns, selected);

  const groupCount = (tools: McpTool[]) =>
    showSelectedCount
      ? `${countSelectedTools(tools, selected)} of ${tools.length} selected`
      : plural(tools.length, "tool");

  const sections: CardSection<McpTool>[] = groups.map(({ server, tools }) => ({
    id: server.name,
    items: tools,
    header: (
      <McpToolkitGroupHeader
        count={<span>{groupCount(tools)}</span>}
        name={server.displayName}
        toolkitId={server.name}
        trailing={
          onOpenServer ? (
            <Button
              onClick={() => onOpenServer(server.name)}
              size="xs"
              variant="ghost"
            >
              Open
            </Button>
          ) : undefined
        }
      />
    ),
  }));

  if (groups.length === 0 && !trailingCard) {
    // No matches at all → empty state. When an unoptimized card trails, that
    // card stands in as the result (mirrors ServerGrid); the scope tabs, not
    // this view, carry cross-tab navigation.
    return <EmptyState action={emptyAction} role="status" title={emptyTitle} />;
  }

  return (
    <VirtualizedCardGrid
      cardRowHeight={TOOL_CARD_ROW_HEIGHT}
      columnBreakpoints={COLUMN_BREAKPOINTS}
      getItemKey={toolKey}
      headerHeight={GROUP_HEADER_HEIGHT}
      label="Tools"
      renderItem={renderTool}
      scrollContainer={scrollContainer}
      sections={sections}
      trailingCard={trailingCard}
    />
  );
}
